import { useCallback } from 'react'
import { Box, Typography } from '@mui/material'
import FavoriteOutlined from '@mui/icons-material/FavoriteOutlined'
import GroupsOutlined from '@mui/icons-material/GroupsOutlined'
import { useOnboardingV2 } from '../OnboardingContext'
import { useWizardData } from '../hooks/useWizardData'
import { useStep } from '../hooks/useStep'
import { stepHeadingSx, stepDescriptionSx } from '../styles'
import SelectableCard from '../components/SelectableCard'
import type { EntityType } from '../types'

const ENTITY_OPTIONS: { value: EntityType; label: string; description: string; icon: React.ReactNode }[] = [
  {
    value: 'charity',
    label: 'Registered Charity',
    description: 'Registered with the NZ Charities Register',
    icon: <FavoriteOutlined />,
  },
  {
    value: 'other',
    label: 'Other Organisation',
    description: 'Non-registered charity, incorporated society, social enterprise, etc',
    icon: <GroupsOutlined />,
  },
]

export default function OrgEntityType() {
  const { goNext } = useOnboardingV2()
  const [wizardData, setWizardData] = useWizardData()

  useStep({
    isValid: !!wizardData.entityType,
  })

  const handleSelect = useCallback(
    (value: string) => {
      const changed = value !== wizardData.entityType
      setWizardData({
        entityType: value as EntityType,
        // Clear stale data from a previous entity type selection
        ...(changed ? { charityDetails: null, orgNumber: '', orgForm: null } : {}),
      })
      setTimeout(() => {
        goNext()
      }, 300)
    },
    [setWizardData, goNext, wizardData.entityType],
  )

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 2 }}>
      <Typography variant="h5" sx={{ ...stepHeadingSx, textAlign: 'center' }}>
        What type of entity are you?
      </Typography>

      <Typography variant="body2" sx={{ ...stepDescriptionSx, textAlign: 'center' }}>
        Select the option that best describes your organisation.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', maxWidth: 480 }}>
        {ENTITY_OPTIONS.map(option => (
          <SelectableCard
            key={option.value}
            label={option.label}
            description={option.description}
            value={option.value}
            icon={option.icon}
            selected={wizardData.entityType === option.value}
            onClick={handleSelect}
            layout="horizontal"
          />
        ))}
      </Box>
    </Box>
  )
}
